import {
  MdsDropdownContentList,
  MdsDropdownItemKind,
  MdsDropdownItem,
} from "@/design-system/components/dropdown";
import { MdsDropdownContent } from "@/design-system/components/dropdown/MdsDropdownContent";
import { FC, useMemo } from "react";
import { SidebarAccountInfo } from "@/components/layout/components/sidebar/SidebarAccountInfo";
import styled from "@emotion/styled";
import { ProfileSize } from "@/components/layout/components/account-profile";
import {
  ProfileDropdownItemsHandlers,
  SyncInfo,
  useProfileDropdownItems,
} from "@/components/layout/components/account-profile/useProfileDropdownItems";
import { PlanId } from "@/store/feature-gates/types";

export type SidebarDropdownMenuContentHandlers = ProfileDropdownItemsHandlers;

export interface SidebarDropdownMenuContentProps {
  className?: string;
  handlers: SidebarDropdownMenuContentHandlers;
  isDebugMode?: boolean;
  accountInfo: {
    profilePhotoUrl: string | null;
    profileDisplayName: string;
    profileEmailAddress: string;
    plan?: PlanId;
  };
  displayAccountInfo: boolean;
  syncInfo: SyncInfo;
}

export const SidebarDropdownMenuContent: FC<SidebarDropdownMenuContentProps> = ({
  className,
  accountInfo,
  displayAccountInfo,
  syncInfo,
  handlers,
  isDebugMode,
}) => {
  const profileDropdownItems = useProfileDropdownItems({
    isDebugMode,
    handlers,
    syncInfo,
  });

  const contentList = useMemo<MdsDropdownContentList>(() => {
    const accountInfoItems = [
      {
        id: "account-info",
        kind: MdsDropdownItemKind.Other,
        content: (
          <AccountInfoWrapper>
            <SidebarAccountInfo
              profileSize={ProfileSize.Large}
              profilePhotoUrl={accountInfo.profilePhotoUrl}
              profileDisplayName={accountInfo.profileDisplayName}
              profileEmailAddress={accountInfo.profileEmailAddress}
              plan={accountInfo.plan}
            />
          </AccountInfoWrapper>
        ),
      },
      { id: "divider-account-info", kind: MdsDropdownItemKind.Divider },
    ] as const;

    const items: MdsDropdownItem[] = [
      ...(displayAccountInfo ? accountInfoItems : []),
      ...profileDropdownItems,
    ];

    return { items };
  }, [
    accountInfo.profileDisplayName,
    accountInfo.profileEmailAddress,
    accountInfo.profilePhotoUrl,
    accountInfo.plan,
    displayAccountInfo,
    profileDropdownItems,
  ]);

  return <MdsDropdownContent className={className} contentList={contentList} />;
};

const AccountInfoWrapper = styled.div`
  padding-inline: ${({ theme }) => theme.spacing["5"]};
  padding-block: ${({ theme }) => theme.spacing["2"]};
`;
